import React from 'react';
import { X } from 'lucide-react';
import type { ConviveOrga } from '../../../lib/organisateurApi';

type Filtre = 'tous' | 'repondu' | 'silencieux';
const APERCU = 8;

const etatDe = (g: ConviveOrga) => g.responded_at ? 'repondu' : g.invitation_sent_at ? 'silencieux' : 'non_invite';
const PILULE: Record<string, { cls: string; label: string }> = {
    repondu: { cls: 'bg-state-ok-soft border-state-ok/30 text-state-ok', label: 'Répondu' },
    silencieux: { cls: 'bg-state-warn-soft border-state-warn/30 text-state-warn', label: 'Sans réponse' },
    non_invite: { cls: 'bg-surface-soft border-border-soft text-content-muted', label: 'Pas encore invité' },
};

/** Le récapitulatif en CSV, fait dans le navigateur à partir de ce que la page affiche déjà — rien de plus. */
const telechargerCsv = (convives: ConviveOrga[], nomDossier: string) => {
    const cellule = (v: string) => `"${String(v || '').replace(/"/g, '""')}"`;
    const lignes = [['Convive', 'Adresse', 'Choix de plat', 'Réponse'].map(cellule).join(';')];
    convives.forEach((g) => lignes.push([g.name, g.email, g.choix || '', PILULE[etatDe(g)].label].map(cellule).join(';')));
    const blob = new Blob([`﻿${lignes.join('\r\n')}`], { type: 'text/csv;charset=utf-8' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `recapitulatif-${nomDossier.replace(/[^\w-]+/g, '-').toLowerCase() || 'convives'}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
};

/**
 * 19b — la liste : filtres à compte, quatre colonnes (Convive, Adresse, Choix de plat, Réponse), un
 * aperçu puis « Voir toute la liste », et le récapitulatif à télécharger. Le filtre « Ne vient pas » de
 * la maquette n'existe pas : un convive ne peut pas décliner dans l'appli (déjà noté au lot 9).
 * Retirer : la croix en bout de ligne ; pour un convive qui a répondu, elle mène à la modale 19c
 * « Ce convive a déjà répondu ».
 */
export function OrganisateurListe({ convives, nomDossier, occupe, onRetirer }: {
    convives: ConviveOrga[];
    nomDossier: string;
    occupe: boolean;
    onRetirer: (g: ConviveOrga) => void;
}) {
    const [filtre, setFiltre] = React.useState<Filtre>('tous');
    const [tout, setTout] = React.useState(false);
    const nb = { tous: convives.length, repondu: convives.filter((g) => g.responded_at).length, silencieux: convives.filter((g) => etatDe(g) === 'silencieux').length };
    const visibles = convives.filter((g) => filtre === 'tous' || etatDe(g) === filtre);
    const lignes = tout ? visibles : visibles.slice(0, APERCU);
    const filtres: { k: Filtre; label: string }[] = [{ k: 'tous', label: 'Tous' }, { k: 'repondu', label: 'A répondu' }, { k: 'silencieux', label: 'Sans réponse' }];

    return (
        <div className="rounded-xl border border-border bg-surface overflow-hidden">
            <div className="flex items-center justify-between gap-3 flex-wrap px-[15px] py-3 bg-surface-soft border-b border-border">
                <div className="flex items-center gap-[7px] flex-wrap">
                    {filtres.map((f) => (
                        <button key={f.k} type="button" onClick={() => setFiltre(f.k)}
                            className={`inline-flex items-center gap-1.5 h-7 px-[11px] rounded-full border text-[12px] font-semibold whitespace-nowrap transition-colors ${filtre === f.k ? 'bg-content border-content text-surface' : 'bg-surface border-border text-content hover:border-content'}`}>
                            {f.label}<span className="tabular-nums opacity-65">{nb[f.k]}</span>
                        </button>
                    ))}
                </div>
                <button type="button" onClick={() => telechargerCsv(convives, nomDossier)} disabled={convives.length === 0} className="text-[12px] font-semibold text-content hover:underline underline-offset-2 whitespace-nowrap disabled:opacity-50">Télécharger le récapitulatif</button>
            </div>

            <div className="overflow-x-auto">
                <div className="min-w-[560px]">
                    <div className="grid grid-cols-[minmax(0,1.5fr)_minmax(0,1.6fr)_minmax(0,1.4fr)_104px_32px] px-[15px] bg-surface-soft border-b border-border">
                        {['Convive', 'Adresse', 'Choix de plat', 'Réponse', ''].map((l, i) => (
                            <div key={i} className={`py-[9px] pr-2 text-[9.5px] font-bold tracking-[.05em] uppercase text-content-muted ${i === 3 ? 'text-right' : ''}`}>{l}</div>
                        ))}
                    </div>
                    {lignes.map((g) => {
                        const e = etatDe(g);
                        return (
                            <div key={g.id} className="grid grid-cols-[minmax(0,1.5fr)_minmax(0,1.6fr)_minmax(0,1.4fr)_104px_32px] items-center px-[15px] border-b border-border-soft">
                                <div className="py-2.5 pr-2 text-[12.5px] font-semibold text-content truncate">{g.name}</div>
                                <div className="py-2.5 pr-2 font-mono text-[11.5px] text-content-muted truncate">{g.email}</div>
                                <div className={`py-2.5 pr-2 text-[12px] truncate ${g.choix ? 'text-content' : 'text-state-warn'}`}>{g.choix || '—'}</div>
                                <div className="py-2 flex justify-end">
                                    <span className={`inline-flex items-center h-[22px] px-[9px] rounded-full border text-[10.5px] font-bold whitespace-nowrap ${PILULE[e].cls}`}>{PILULE[e].label}</span>
                                </div>
                                <div className="flex justify-end">
                                    <button type="button" onClick={() => onRetirer(g)} disabled={occupe} title={e === 'repondu' ? 'Ce convive a déjà répondu' : 'Retirer de la liste'}
                                        className="w-7 h-7 rounded-full flex items-center justify-center text-content-muted hover:text-state-danger hover:bg-state-danger-soft transition-colors disabled:opacity-40"><X size={13} /></button>
                                </div>
                            </div>
                        );
                    })}
                    {lignes.length === 0 && <p className="px-[15px] py-6 text-[12.5px] text-content-muted text-center">Aucun convive dans cette vue.</p>}
                </div>
            </div>

            <div className="flex items-center justify-between gap-3 flex-wrap px-[15px] py-[11px] bg-surface-soft">
                <span className="text-[11.5px] text-content-muted">{lignes.length} ligne{lignes.length > 1 ? 's' : ''} sur {visibles.length}</span>
                {visibles.length > APERCU && (
                    <button type="button" onClick={() => setTout((v) => !v)} className="text-[12px] font-semibold text-content hover:underline underline-offset-2">{tout ? "Réduire l'aperçu" : 'Voir toute la liste'}</button>
                )}
            </div>
        </div>
    );
}
